// src/services/messageService.js
import api from './api';
import { db } from '../firebase/config';
import { 
  ref, set, push, onValue, 
  query, orderByChild, equalTo, 
  get, update, off
} from 'firebase/database';
import { friendEvents } from '../components/SocialMenu';

// Firebase references
const getMessagesRef = () => ref(db, 'messages');
const getConversationsRef = (walletAddress) => 
  ref(db, `conversations/${walletAddress}`);

// Create a message in Firebase
export const sendMessage = async (senderWallet, receiverWallet, content) => {
  try {
    // Check for links before sending (keeping your existing security feature)
    if (containsLinks(content)) {
      throw new Error('Links are not allowed in messages');
    }
    
    // Sanitize content as an extra safety measure
    const sanitizedContent = sanitizeText(content);
    
    // Generate a new message ID
    const newMessageRef = push(getMessagesRef());
    
    const messageData = {
      id: newMessageRef.key,
      senderWallet,
      receiverWallet,
      content: sanitizedContent,
      createdAt: Date.now(),
      read: false
    };
    
    // Save message to Firebase
    await set(newMessageRef, messageData);
    
    // Update conversation metadata for both users
    const senderConversationRef = ref(db, `conversations/${senderWallet}/${receiverWallet}`);
    const receiverConversationRef = ref(db, `conversations/${receiverWallet}/${senderWallet}`);
    
    const conversationData = {
      lastMessage: sanitizedContent,
      timestamp: Date.now(),
      lastMessageId: newMessageRef.key
    };
    
    await update(senderConversationRef, conversationData);
    await update(receiverConversationRef, {
      ...conversationData,
      unreadCount: (await get(receiverConversationRef).then(snap => 
        snap.exists() ? (snap.val().unreadCount || 0) : 0)) + 1
    });
    
    return messageData;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Get conversation messages between two users
export const getConversation = async (walletAddress, friendWallet) => {
  try {
    // Create two queries - one for messages sent, one for received
    const sentQuery = query(
      getMessagesRef(),
      orderByChild('senderWallet'),
      equalTo(walletAddress)
    );
    
    const receivedQuery = query(
      getMessagesRef(),
      orderByChild('receiverWallet'),
      equalTo(walletAddress)
    );
    
    // Execute both queries
    const [sentSnapshot, receivedSnapshot] = await Promise.all([
      get(sentQuery),
      get(receivedQuery)
    ]);
    
    // Process results
    const messages = [];
    
    sentSnapshot.forEach(childSnapshot => {
      const message = childSnapshot.val();
      if (message.receiverWallet === friendWallet) {
        messages.push(message);
      }
    });
    
    receivedSnapshot.forEach(childSnapshot => {
      const message = childSnapshot.val();
      if (message.senderWallet === friendWallet) {
        messages.push(message);
      }
    });
    
    // Sort by timestamp
    messages.sort((a, b) => a.createdAt - b.createdAt);
    
    return messages;
  } catch (error) {
    console.error('Error fetching conversation:', error);
    throw error;
  }
};

// Listen for new messages in a conversation
export const subscribeToConversation = (walletAddress, friendWallet, callback) => {
  const messagesRef = getMessagesRef();
  
  const messageListener = onValue(messagesRef, (snapshot) => {
    const messages = [];
    snapshot.forEach((childSnapshot) => {
      const message = childSnapshot.val();
      
      // Only include messages between these two users
      if ((message.senderWallet === walletAddress && message.receiverWallet === friendWallet) ||
          (message.senderWallet === friendWallet && message.receiverWallet === walletAddress)) {
        messages.push(message);
      }
    });
    
    // Sort by timestamp
    messages.sort((a, b) => a.createdAt - b.createdAt);
    callback(messages);
  });
  
  // Return unsubscribe function
  return () => off(messagesRef, 'value', messageListener);
};

// Mark messages as read
export const markMessagesAsRead = async (walletAddress, friendWallet) => {
  try {
    // Get all unread messages from this friend
    const messagesRef = query(
      getMessagesRef(),
      orderByChild('senderWallet'),
      equalTo(friendWallet)
    );
    
    const snapshot = await get(messagesRef);
    const updates = {};
    
    snapshot.forEach((childSnapshot) => {
      const message = childSnapshot.val();
      if (message.receiverWallet === walletAddress && !message.read) {
        updates[`${childSnapshot.key}/read`] = true;
      }
    });
    
    // Update the messages if there are any unread
    if (Object.keys(updates).length > 0) {
      await update(getMessagesRef(), updates);
    }
    
    // Reset unread count in the conversation metadata
    const conversationRef = ref(db, `conversations/${walletAddress}/${friendWallet}`);
    await update(conversationRef, { unreadCount: 0 });
    
    return { success: true };
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

// Get all conversations for a user
export const getAllConversations = async (walletAddress) => {
  try {
    const conversationsRef = getConversationsRef(walletAddress);
    const snapshot = await get(conversationsRef);
    
    if (!snapshot.exists()) {
      return [];
    }
    
    const conversations = [];
    const conversationsData = snapshot.val();
    
    // Convert object to array and add the friend wallet as a property
    for (const friendWallet in conversationsData) {
      // Get friend details from API
      try {
        const friendResponse = await api.get(`/users/${friendWallet}`);
        const friend = friendResponse.data;
        
        conversations.push({
          friend: {
            walletAddress: friendWallet,
            username: friend.username,
            character: friend.character
          },
          lastMessage: conversationsData[friendWallet].lastMessage,
          timestamp: conversationsData[friendWallet].timestamp,
          unreadCount: conversationsData[friendWallet].unreadCount || 0
        });
      } catch (error) {
        console.error(`Error fetching friend data for ${friendWallet}:`, error);
        conversations.push({
          friend: {
            walletAddress: friendWallet,
            username: friendWallet.substring(0, 6) + '...',
          },
          lastMessage: conversationsData[friendWallet].lastMessage,
          timestamp: conversationsData[friendWallet].timestamp,
          unreadCount: conversationsData[friendWallet].unreadCount || 0
        });
      }
    }
    
    // Sort by timestamp (newest first)
    conversations.sort((a, b) => b.timestamp - a.timestamp);
    
    return conversations;
  } catch (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }
};

// Listen for new conversations and updates
export const subscribeToConversations = (walletAddress, callback) => {
  const conversationsRef = getConversationsRef(walletAddress);
  
  const listener = onValue(conversationsRef, async (snapshot) => {
    try {
      if (!snapshot.exists()) {
        callback([]);
        return;
      }
      
      const conversations = [];
      const conversationsData = snapshot.val();
      
      // Process each conversation
      for (const friendWallet in conversationsData) {
        try {
          const friendResponse = await api.get(`/users/${friendWallet}`);
          const friend = friendResponse.data;
          
          conversations.push({
            friend: {
              walletAddress: friendWallet,
              username: friend.username,
              character: friend.character
            },
            lastMessage: conversationsData[friendWallet].lastMessage,
            timestamp: conversationsData[friendWallet].timestamp,
            unreadCount: conversationsData[friendWallet].unreadCount || 0
          });
        } catch (error) {
          console.error(`Error fetching friend data for ${friendWallet}:`, error);
          conversations.push({
            friend: {
              walletAddress: friendWallet,
              username: friendWallet.substring(0, 6) + '...',
            },
            lastMessage: conversationsData[friendWallet].lastMessage,
            timestamp: conversationsData[friendWallet].timestamp,
            unreadCount: conversationsData[friendWallet].unreadCount || 0
          });
        }
      }
      
      // Sort by timestamp (newest first)
      conversations.sort((a, b) => b.timestamp - a.timestamp);
      
      callback(conversations);
    } catch (error) {
      console.error('Error processing conversations:', error);
    }
  });
  
  // Return unsubscribe function
  return () => off(conversationsRef, 'value', listener);
};

// Utility functions for link detection and sanitation (keeping your existing security features)
const URL_PATTERNS = [
  /https?:\/\/[^\s]+/gi,        // http:// or https:// links
  /www\.[^\s]+\.[^\s]+/gi,      // www.domain.com
  /[^\s@]+\.[a-z]{2,}(?:\/[^\s]*)*/gi  // domain.com or domain.com/path
];

const containsLinks = (text) => {
  if (typeof text !== 'string') return false;
  return URL_PATTERNS.some(pattern => pattern.test(text));
};

const sanitizeText = (text) => {
  if (typeof text !== 'string') return text;
  
  let sanitized = text;
  URL_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '[link removed]');
  });
  return sanitized;
};